import React, { useEffect, useState } from 'react';

import { Ellipsis, Loader } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-toastify';

import { AddedIcon, AddIcon, CopyIcon, LinkedInIcon } from '#/assets/svgs';
import { Filters, SortableTableHeader, CompanySocialLinks } from '#/common/components';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from '#/components/ui';
import { apiEndpoints, queryKeys, useInfiniteScrollQuery, usePostMutation } from '#/services';
import { capitalizeWords, DECISION_MAKERS_TABLE_HEADERS, EMPLOYEES_TABLE_COL_GROUP } from '#/utils';

const Employees = ({ employeeFilters }) => {
    const [queryParams, setQueryParams] = useState({});

    const { ref, inView } = useInView();

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteScrollQuery({
        key: queryKeys.EMPLOYEES,
        url: apiEndpoints.GET_EMPLOYEES,
        params: queryParams,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);
    // TODO: WE MAY NEED THIS LINE LATER
    // const decision_makers = data?.pages?.flatMap(page => page.results) ?? [];
    const decision_makers =
        Array.from(
            (data?.pages || [])
                ?.flatMap(p => p.results)
                ?.reduce((map, dm) => {
                    if (!map.has(dm.id)) map.set(dm.id, dm);
                    return map;
                }, new Map())
                ?.values()
        ) || [];

    const { mutate: addToContacts } = usePostMutation({
        url: apiEndpoints.ADD_TO_CONTACT,
        queryKey: [queryKeys.USER, queryKeys.EMPLOYEES],
        successMessage: 'Added to contact successfully!',
    });

    const handleClear = () => {
        setQueryParams({});
    };
    const handleAddToContacts = id => () => {
        addToContacts({ payload: { employee: id } });
    };

    return (
        <>
            <Filters
                queryParams={queryParams}
                filtersData={employeeFilters}
                setQueryParams={setQueryParams}
                onClearFilters={handleClear}
            />
            <div className='overflow-x-auto'>
                <Table className='w-full table-fixed'>
                    {EMPLOYEES_TABLE_COL_GROUP}
                    <TableHeader className='bg-white sticky top-0 z-10'>
                        <SortableTableHeader headers={DECISION_MAKERS_TABLE_HEADERS} setQueryParams={setQueryParams} />
                    </TableHeader>
                </Table>

                <div className='h-[calc(100dvh-400px)] xl:h-[calc(100dvh-310px)] overflow-y-auto pl-2'>
                    <Table className='w-full table-fixed'>
                        {EMPLOYEES_TABLE_COL_GROUP}
                        <TableBody className='w-full'>
                            {isLoading ? (
                                <TableRow className='border-none'>
                                    <TableCell colSpan={5} className='h-[300px] text-center'>
                                        <Loader className='mx-auto animate-spin text-muted-foreground' />
                                    </TableCell>
                                </TableRow>
                            ) : decision_makers?.length > 0 ? (
                                decision_makers.map(decisionMaker => (
                                    <TableRow
                                        key={decisionMaker?.id}
                                        className='min-h-[72px] hover:bg-transparent cursor-default'
                                    >
                                        <TableCell className='flex items-center gap-2 min-h-[72px]'>
                                            <Avatar className='w-10 h-10'>
                                                <AvatarImage
                                                    src={decisionMaker?.profilePicture}
                                                    alt={decisionMaker?.firstName}
                                                />
                                                <AvatarFallback className='bg-gray200 text-gray500'>
                                                    {decisionMaker?.firstName?.charAt(0)}
                                                    {decisionMaker?.lastName?.charAt(0)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className='flex flex-col min-w-0 flex-1'>
                                                <span className='whitespace-normal break-words text-gray900 text-sm font-medium capitalize'>
                                                    {decisionMaker?.firstName} {decisionMaker?.lastName}
                                                </span>
                                                {decisionMaker?.email && (
                                                    <div className='flex flex-row flex-wrap items-center gap-2'>
                                                        <span className='max-w-[200px] truncate text-sm text-gray500 font-normal'>
                                                            {decisionMaker?.email}
                                                        </span>
                                                        <CopyIcon
                                                            className='cursor-pointer'
                                                            onClick={() => {
                                                                const email = decisionMaker?.email || '';
                                                                navigator.clipboard.writeText(email).then(() => {
                                                                    toast.success('Email copied to clipboard!');
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                {decisionMaker?.linkedinUrl && (
                                                    <a
                                                        href={decisionMaker?.linkedinUrl}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='no-underline'
                                                    >
                                                        <div className='flex flex-row items-center gap-[6px] mt-[2px]'>
                                                            <LinkedInIcon className={'w-4 h-4'} />
                                                            <span className='max-w-[200px] truncate underline text-[12px] text-[#007AB9] font-normal'>
                                                                Linkedin
                                                            </span>
                                                        </div>
                                                    </a>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className='whitespace-normal break-words text-gray500 text-sm pl-3 font-normal'>
                                            {capitalizeWords(decisionMaker?.company?.name)}
                                        </TableCell>
                                        <TableCell className='whitespace-normal break-words text-gray500 text-sm font-normal pl-1'>
                                            {decisionMaker?.title || '-'}
                                        </TableCell>

                                        <TableCell className='pl-8' onClick={e => e.stopPropagation()}>
                                            <CompanySocialLinks socialLinks={decisionMaker?.company?.socialLinks} />
                                        </TableCell>
                                        <TableCell className='pl-5'>
                                            <Button
                                                size='sm'
                                                variant='primary'
                                                className={`rounded-md px-4 ${decisionMaker?.isInContactList ? 'bg-blueLight50 text-blueLight700 ' : 'bg-blueGray50 text-blueGray700'}`}
                                                onClick={handleAddToContacts(decisionMaker?.id)}
                                            >
                                                {decisionMaker?.isInContactList ? (
                                                    <div className='flex items-center flex-row gap-[2px]'>
                                                        <AddedIcon className='text-blueLight700' />
                                                        <p>Added</p>
                                                    </div>
                                                ) : (
                                                    <div className='flex items-center flex-row gap-1'>
                                                        <AddIcon className='text-blueGray700' />
                                                        <p>Add</p>
                                                    </div>
                                                )}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow className='border-none hover:bg-white cursor-default'>
                                    <TableCell colSpan={5} className='h-[300px] text-center text-gray-400'>
                                        No decision maker found
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow className='pointer-events-none'>
                                <TableCell colSpan={5}>
                                    <div ref={ref} id='sentinel' className='py-6 flex justify-center text-center'>
                                        {isFetchingNextPage && <Ellipsis className='animate-ping' />}
                                    </div>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
};

export { Employees };
